import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { colorDeEstatusOrden, colorDeEstatusFactura } from '@/shared/lib/coloresDeEstatus'
import { Checkbox } from '@/shared/components/Checkbox'
import { useState, useEffect } from 'react'
import { ArrowLeft, FileText, Download, CreditCard, Upload, X, MoreVertical, Eye, CheckCircle2, XCircle, Info, ChevronUp, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import { purchaseOrdersService } from '@/features/proveedores/api/purchaseOrders.service'
import BloqueoFacturacion, { type BloqueoFacturacionError } from '@/features/proveedores/components/BloqueoFacturacion'
import { supplierAuthService } from '@/features/proveedores/api/supplierAuthService'
import { companyInfoService } from '@/features/configuracion/api/companyInfo.service'
import { getSupplierProfile } from '@/features/proveedores/api/supplierProfile.service'
import { pasoDeFleteResuelto } from '@/features/proveedores/lib/documentosDeFlete'
import { ArchivoDeFlete } from '@/features/proveedores/components/ArchivoDeFlete'
import type { PurchaseOrder, PurchaseOrderInvoice, PurchaseOrderAttachment, PurchaseOrderPayment, PurchaseOrderLine } from '@/types/purchaseOrder.types'
import { InvoiceSharedCells, getInvoiceStatusMeta } from '@/features/proveedores/components/InvoiceSharedCells'
import { etiquetaDeFactura } from '@/features/proveedores/lib/estadoDeFactura'
import { TruncatedText } from '@/shared/components/TruncatedText'
import { formatDate } from '@/shared/lib/dateUtils'
import { Badge } from '@/shared/components/Badge'
import type { ColorDeBadge } from '@/shared/components/Badge'
import { nombreDeUsuario } from '@/shared/lib/nombreDeUsuario';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

const CalendarFolded = ({ className, ...props }: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M16 3H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12l5-5V5a2 2 0 0 0-2-2Z" />
    <path d="M21 14h-5a2 2 0 0 0-2 2v5" />
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <path d="M2 9h19" />
  </svg>
);

interface PurchaseOrderDetailProps {
  orderId: string
  onBack: () => void
}

const PurchaseOrderDetail = ({ orderId, onBack }: PurchaseOrderDetailProps) => {
  // Error de bloqueo de facturación (422 con `tipo` del backend). Guarda el
  // objeto completo; el componente decide el mensaje/CTA según el tipo.
  // null = sin bloqueo activo.
  const [bloqueoError, setBloqueoError] = useState<BloqueoFacturacionError | null>(null)

  /**
   * Revisa si puede facturar ANTES de abrir el modal de subir.
   *
   * Antes el proveedor se enteraba al final: elegía el XML, esperaba la
   * validación y hasta el submit le llegaba el 422. Ahora se le dice antes de
   * que llene nada. El 422 sigue puesto por si se bloquea entre el clic y el
   * envío.
   */
  const puedeFacturar = async (): Promise<boolean> => {
    const estado = await purchaseOrdersService.getEstadoDeFacturacion()
    if (!estado.bloqueado) return true
    const { bloqueado: _ignorado, ...cuerpo } = estado
    setBloqueoError(cuerpo)
    return false
  }
  const [order, setOrder] = useState<PurchaseOrder | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [invoices, setInvoices] = useState<PurchaseOrderInvoice[]>([])
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false)
  const [attachments, setAttachments] = useState<PurchaseOrderAttachment[]>([])
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false)
  // const [comments, setComments] = useState<PurchaseOrderComment[]>([])
  // const [isLoadingComments, setIsLoadingComments] = useState(false)
  const [openAttachmentMenu, setOpenAttachmentMenu] = useState<string | null>(null)

  const [activeTab, setActiveTab] = useState<'lines' | 'payments' | 'invoices'>('lines')
  const [payments, setPayments] = useState<PurchaseOrderPayment[]>([])
  const [isLoadingPayments, setIsLoadingPayments] = useState(false)
  const [showInvoiceReviewNotice, setShowInvoiceReviewNotice] = useState(false)

  // Invoice Upload State
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set())
  const [availableLineIds, setAvailableLineIds] = useState<Set<string>>(new Set())
  const [isRefreshingLineAvailability, setIsRefreshingLineAvailability] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadXml, setUploadXml] = useState<File | null>(null)
  const [uploadPdf, setUploadPdf] = useState<File | null>(null)
  /**
   * Paso de documentos de flete (SP-221). Esta pantalla tenía un toggle de Carta
   * Porte que no mandaba nada, así que el servidor rechazaba la factura de un
   * proveedor de Fletes pidiendo documentos que aquí nunca se podían adjuntar.
   */
  const [esFletes, setEsFletes] = useState(false)
  const [cartaPorteAplica, setCartaPorteAplica] = useState(false)
  const [cartaPorteXml, setCartaPorteXml] = useState<File | null>(null)
  const [cartaPortePdf, setCartaPortePdf] = useState<File | null>(null)
  const [remisionFirmada, setRemisionFirmada] = useState<File | null>(null)
  const [hojaEntregas, setHojaEntregas] = useState<File | null>(null)

  // Solo el portal pregunta por el tipo: adentro podría quedar un token de
  // proveedor viejo y el paso aparecería con datos de alguien más.
  const enPortalDeProveedor = window.location.pathname.startsWith('/proveedores')
  useEffect(() => {
    if (!enPortalDeProveedor) {
      setEsFletes(false)
      return
    }
    getSupplierProfile()
      .then((perfil) => setEsFletes(perfil.general?.supplierType === 'fletes'))
      .catch(() => setEsFletes(false))
  }, [enPortalDeProveedor])

  const fleteResuelto = pasoDeFleteResuelto(esFletes, {
    cartaPorteAplica,
    cartaPorteXml,
    cartaPortePdf,
    remisionFirmada,
    hojaEntregas,
  })

  const limpiarDocumentosDeFlete = () => {
    setCartaPorteAplica(false)
    setCartaPorteXml(null)
    setCartaPortePdf(null)
    setRemisionFirmada(null)
    setHojaEntregas(null)
  }
  // TODO(back): bloqueo por facturas pagadas sin CFDI de complemento (SP-228/223).
  // El conteo/lista viene del backend; null = sin bloqueo (por ahora).
  const [complementoBloqueo] = useState<{ count: number } | null>(null)
  const [isUploadingInvoice, setIsUploadingInvoice] = useState(false)
  const [replaceInvoiceId, setReplaceInvoiceId] = useState<string | null>(null)
  const [xmlValidationResult, setXmlValidationResult] = useState<{
    xmlReadable: boolean
    estructuraCorrecta: boolean
    rfcEmisorCorrecto: boolean
    rfcReceptorCorrecto: boolean
    montoValido: boolean
    xmlTotal: number | null
  } | null>(null)
  const [isValidatingXml, setIsValidatingXml] = useState(false)
  const [isValidationPanelOpen, setIsValidationPanelOpen] = useState(true)
  const [companyRfc, setCompanyRfc] = useState<string | null>(null)

  // Payment Complement Upload State
  const [showPaymentUploadModal, setShowPaymentUploadModal] = useState(false)
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null)
  const [uploadPaymentXml, setUploadPaymentXml] = useState<File | null>(null)
  const [uploadPaymentPdf, setUploadPaymentPdf] = useState<File | null>(null)
  const [isUploadingPaymentComplement, setIsUploadingPaymentComplement] = useState(false)

  // Invoice Lines Modal State
  const [showInvoiceLinesModal, setShowInvoiceLinesModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null)

  // Order Action States
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [confirmComment, setConfirmComment] = useState('')
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  useEffect(() => {
    loadOrderDetail()
  }, [orderId])

  useEffect(() => {
    if (order) {
      loadInvoices()
      loadAttachments()
      // loadComments()
      loadPayments()
    }
  }, [order])

  useEffect(() => {
    if (showUploadModal && companyRfc === null) {
      companyInfoService.getForSupplier()
        .then((info) => setCompanyRfc(info.rfc))
        .catch(() => setCompanyRfc(''))
    }
  }, [showUploadModal])

  const loadOrderDetail = async () => {
    setIsLoading(true)
    try {
      const orderData = await purchaseOrdersService.getOrderById(orderId)
      setOrder(orderData)
    } catch (error) {
      console.error('Error loading order detail:', error)
      setOrder(null)
    } finally {
      setIsLoading(false)
    }
  }

  const loadInvoices = async () => {
    setIsLoadingInvoices(true)
    try {
      const data = await purchaseOrdersService.getInvoices(orderId)
      setInvoices(data)
    } catch (error) {
      console.error('Error loading invoices:', error)
    } finally {
      setIsLoadingInvoices(false)
    }
  }

  const loadAttachments = async () => {
    setIsLoadingAttachments(true)
    try {
      const data = await purchaseOrdersService.getAttachments(orderId)
      setAttachments(data)
    } catch (error) {
      console.error('Error loading attachments:', error)
    } finally {
      setIsLoadingAttachments(false)
    }
  }

  /*
  const loadComments = async () => {
    setIsLoadingComments(true)
    try {
      const data = await purchaseOrdersService.getComments(orderId)
      setComments(data)
    } catch (error) {
      console.error('Error loading comments:', error)
    } finally {
      setIsLoadingComments(false)
    }
  }
  */

  const loadPayments = async () => {
    setIsLoadingPayments(true)
    try {
      const data = await purchaseOrdersService.getAllPayments(orderId)
      setPayments(data)
    } catch (error) {
      console.error('Error loading payments:', error)
    } finally {
      setIsLoadingPayments(false)
    }
  }

  const refreshAvailableLines = async (showErrorToast = false): Promise<Set<string> | null> => {
    setIsRefreshingLineAvailability(true)
    try {
      const availableLines = await purchaseOrdersService.getAvailableLines(orderId)
      const availableIds = new Set(availableLines.map(line => line.id))
      setAvailableLineIds(availableIds)
      setSelectedLineIds((prev) => {
        const filtered = new Set<string>()
        prev.forEach((id) => {
          if (availableIds.has(id)) filtered.add(id)
        })
        return filtered
      })
      return availableIds
    } catch (error) {
      console.error('Error refreshing available lines:', error)
      if (showErrorToast) {
        toast.error('Error al actualizar disponibilidad de líneas')
      }
      return null
    } finally {
      setIsRefreshingLineAvailability(false)
    }
  }

  useEffect(() => {
    if (!order || activeTab !== 'lines') return

    refreshAvailableLines()
    const interval = setInterval(() => {
      refreshAvailableLines()
    }, 10000)

    return () => clearInterval(interval)
  }, [order?.id, orderId, activeTab])

  const formatCurrency = (amount: number, currency = 'MXN') => {
    const code = (currency || 'MXN').toUpperCase()
    const parsed = Number(amount)
    const value = Number.isFinite(parsed) ? parsed : 0
    return `${code} $${value.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`
  }

  const parseCfdiXmlTotal = async (xmlFile: File): Promise<number | null> => {
    try {
      const xmlText = await xmlFile.text()
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(xmlText, 'application/xml')
      const parserError = xmlDoc.getElementsByTagName('parsererror')
      if (parserError.length > 0) return null

      const root = xmlDoc.documentElement
      const totalAttr = root?.getAttribute('Total') || root?.getAttribute('total')
      if (!totalAttr) return null

      const parsed = Number(totalAttr)
      return Number.isFinite(parsed) ? parsed : null
    } catch {
      return null
    }
  }

  const validateCfdiXml = async (xmlFile: File) => {
    setIsValidatingXml(true)
    setXmlValidationResult(null)
    try {
      const xmlText = await xmlFile.text()
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(xmlText, 'application/xml')
      const parserError = xmlDoc.getElementsByTagName('parsererror')
      if (parserError.length > 0) {
        setXmlValidationResult({ xmlReadable: false, estructuraCorrecta: false, rfcEmisorCorrecto: false, rfcReceptorCorrecto: false, montoValido: false, xmlTotal: null })
        return
      }

      const root = xmlDoc.documentElement
      const isCfdi =
        (root.getAttribute('xmlns:cfdi') || '').includes('sat.gob.mx') ||
        root.localName === 'Comprobante' ||
        root.nodeName.includes('Comprobante')

      const emisorEl = xmlDoc.getElementsByTagNameNS('*', 'Emisor')[0]
      const receptorEl = xmlDoc.getElementsByTagNameNS('*', 'Receptor')[0]
      const estructuraCorrecta = isCfdi && !!emisorEl && !!receptorEl

      const xmlEmisorRfc = (emisorEl?.getAttribute('Rfc') || emisorEl?.getAttribute('RFC') || '').toUpperCase()
      const xmlReceptorRfc = (receptorEl?.getAttribute('Rfc') || receptorEl?.getAttribute('RFC') || '').toUpperCase()

      const supplierRfc = (supplierAuthService.getSupplierData()?.rfc || '').toUpperCase()
      const rfcEmisorCorrecto = !!xmlEmisorRfc && !!supplierRfc && xmlEmisorRfc === supplierRfc

      // Si ya tenemos el RFC de la empresa lo comparamos directamente;
      // si todavía no se cargó, validamos solo el formato para no bloquear al usuario.
      const rfcReceptorCorrecto = companyRfc
        ? !!xmlReceptorRfc && xmlReceptorRfc === companyRfc.toUpperCase()
        : /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(xmlReceptorRfc)

      // Validación de monto: Total del XML vs suma de líneas seleccionadas
      const totalAttr = root?.getAttribute('Total') || root?.getAttribute('total')
      const xmlTotal = totalAttr !== null ? Number(totalAttr) : null
      let montoValido = false
      if (replaceInvoiceId) {
        // En flujo de reemplazo no hay líneas seleccionadas, saltamos la validación de monto
        montoValido = xmlTotal !== null && Number.isFinite(xmlTotal)
      } else {
        const currentSelectedAmount = (order?.lines || [])
          .filter((line) => selectedLineIds.has(line.id))
          .reduce((sum, line) => sum + Number(line.total || 0), 0)
        const tolerance = 0.05
        montoValido =
          xmlTotal !== null &&
          Number.isFinite(xmlTotal) &&
          Math.abs(xmlTotal - currentSelectedAmount) <= tolerance
      }

      setXmlValidationResult({ xmlReadable: true, estructuraCorrecta, rfcEmisorCorrecto, rfcReceptorCorrecto, montoValido, xmlTotal })
    } catch {
      setXmlValidationResult({ xmlReadable: false, estructuraCorrecta: false, rfcEmisorCorrecto: false, rfcReceptorCorrecto: false, montoValido: false, xmlTotal: null })
    } finally {
      setIsValidatingXml(false)
    }
  }

  const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount), 0)
  const orderTotal = Number(order?.total || 0)
  const isPartiallyOrUnpaid = orderTotal > 0 && totalPaid < orderTotal
  const isFulfilled = String((order as any)?.fulfillment_status || '').toLowerCase() === 'surtido'
  const availableLines = (order?.lines || []).filter((line) => availableLineIds.has(line.id))
  const availableAmount = availableLines.reduce((sum, line) => sum + Number(line.total || 0), 0)
  const selectedAmount = (order?.lines || [])
    .filter((line) => selectedLineIds.has(line.id))
    .reduce((sum, line) => sum + Number(line.total || 0), 0)
  const eligibleLinesForNewInvoice = (order?.lines || []).filter((line: any) => {
    if (line.erp_status !== 'LIBERADA_PARA_PROVEEDOR') return false
    const activeInvoices = (line.invoices || []).filter((inv: any) =>
      ['received', 'in_review', 'approved'].includes(inv.status),
    )
    return activeInvoices.length === 0
  })
  const canUploadAnotherInvoice = eligibleLinesForNewInvoice.length > 0
  const hasApprovedInvoice = invoices.some((invoice) => invoice.status === 'approved')

  const statusDisplay = (() => {
    if (order?.status === 'completed' && isPartiallyOrUnpaid) {
      if (isFulfilled) {
        return {
          label: 'Surtida',
          color: 'verde' as ColorDeBadge
        }
      }

      return {
        label: 'Pendiente de pago',
        color: 'amarillo' as ColorDeBadge
      }
    }

    return {
      label: purchaseOrdersService.getStatusLabel(order?.status || ''),
      color: colorDeEstatusOrden(order?.status || '')
    }
  })()

  /**
   * Entra o sale del modo de seleccionar lineas para facturar.
   *
   * Al ENTRAR revisa primero si puede facturar: si esta bloqueado, se le dice
   * ahi mismo y ni siquiera se le deja elegir lineas. Al salir no se revisa
   * nada —cancelar siempre tiene que funcionar, pase lo que pase con la
   * consulta.
   */
  const toggleSelectionMode = async () => {
    if (!isSelectionMode && !(await puedeFacturar())) return
    setIsSelectionMode(!isSelectionMode)
    setSelectedLineIds(new Set())
    setAvailableLineIds(new Set()) // Clear available lines when exiting selection mode
    setActiveTab('lines')
  }

  const toggleLineSelection = (lineId: string) => {
    const newSelected = new Set(selectedLineIds)
    if (newSelected.has(lineId)) {
      newSelected.delete(lineId)
    } else {
      newSelected.add(lineId)
    }
    setSelectedLineIds(newSelected)
  }

  const toggleSelectAll = () => {
    if (!order) return

    const allLines = order.lines || []
    if (allLines.length === 0) return

    // Filter only available lines
    const availableLinesToSelect = allLines.filter(l => availableLineIds.has(l.id))

    // Check if all *available* lines are selected
    // Note: We check against availableLinesToSelect.length, not allLines.length
    const areAllAvailableSelected = availableLinesToSelect.length > 0 &&
      availableLinesToSelect.every(l => selectedLineIds.has(l.id))

    if (areAllAvailableSelected) {
      setSelectedLineIds(new Set())
    } else {
      // Select all available lines
      const newSelected = new Set<string>()
      availableLinesToSelect.forEach(l => newSelected.add(l.id))
      setSelectedLineIds(newSelected)
    }
  }

  const handleRowClick = async (lineId: string) => {
    if (!isSelectionMode) {
      const loadingToast = toast.loading('Habilitando selección...')
      try {
        const availableIds = await refreshAvailableLines()
        if (!availableIds) {
          toast.error('Error al cargar las líneas disponibles', { id: loadingToast })
          return
        }
        // Selection mode enabled

        setIsSelectionMode(true)
        if (availableIds.has(lineId)) {
          setSelectedLineIds(new Set([lineId]))
        } else {
          toast('Esta línea no está disponible para facturar', { icon: 'ℹ️', id: loadingToast })
          // Still unblock selection mode even if clicked line wasn't available
        }
        if (availableIds.size === 0) {
          toast('No hay líneas disponibles para facturar', { icon: 'ℹ️', id: loadingToast })
        } else {
          toast.dismiss(loadingToast)
        }

      } catch (error) {
        console.error('Error loading available lines:', error)
        toast.error('Error al cargar las líneas disponibles', { id: loadingToast })
      }
    } else {
      // Only allow toggling if line is available
      if (availableLineIds.has(lineId)) {
        toggleLineSelection(lineId)
      } else {
        toast('Esta línea ya no está disponible para facturar. Actualizando disponibilidad de líneas...', { icon: 'ℹ️' })
        refreshAvailableLines()
      }
    }
  }

  const handleUploadInvoice = async () => {
    if (!order || !uploadXml) return

    setIsUploadingInvoice(true)
    try {
      // Flujo de reemplazo: PATCH sobre la factura existente
      if (replaceInvoiceId) {
        const updated = await purchaseOrdersService.replaceInvoice(
          order.id,
          replaceInvoiceId,
          uploadXml,
          uploadPdf || undefined
        )
        setInvoices(prev => prev.map(inv => inv.id === replaceInvoiceId ? { ...inv, ...updated } : inv))
        toast.success('Factura actualizada. Estatus: Recibida')
        setShowUploadModal(false)
        setUploadXml(null)
        setUploadPdf(null)
        setReplaceInvoiceId(null)
        return
      }

      // Flujo normal: POST nueva factura
      const refreshedAvailableIds = await refreshAvailableLines(true)
      if (!refreshedAvailableIds) {
        return
      }

      const selectedIds = Array.from(selectedLineIds).filter((id) => refreshedAvailableIds.has(id))
      if (selectedIds.length === 0) {
        toast.error('No hay líneas habilitadas disponibles para facturar')
        return
      }

      const selectedLinesTotal = (order.lines || [])
        .filter((line) => selectedIds.includes(line.id))
        .reduce((sum, line) => sum + Number(line.total || 0), 0)

      const xmlTotal = await parseCfdiXmlTotal(uploadXml)
      if (xmlTotal === null) {
        toast.error('No se pudo validar el monto del XML. Verifica que sea un CFDI válido.')
        return
      }

      const tolerance = 0.05
      if (xmlTotal - selectedLinesTotal > tolerance) {
        toast.error(
          `El monto del XML (${formatCurrency(xmlTotal, order.currency)}) excede el monto habilitado (${formatCurrency(selectedLinesTotal, order.currency)}).`,
          { duration: 6000 }
        )
        return
      }
      if (selectedLinesTotal - xmlTotal > tolerance) {
        toast.error(
          `El monto del XML (${formatCurrency(xmlTotal, order.currency)}) es menor al monto de las líneas seleccionadas (${formatCurrency(selectedLinesTotal, order.currency)}).`,
          { duration: 6000 }
        )
        return
      }

      await purchaseOrdersService.adjustInvoice(
        order.id,
        uploadXml,
        selectedIds,
        uploadPdf || undefined,
        esFletes
          ? { cartaPorteAplica, cartaPorteXml, cartaPortePdf, remisionFirmada, hojaEntregas }
          : undefined
      )

      toast.success('Factura cargada. Estatus: Recibida')
      setShowUploadModal(false)
      setUploadXml(null)
      limpiarDocumentosDeFlete()
      setUploadPdf(null)
      setSelectedLineIds(new Set())
      setIsSelectionMode(false)
      setShowInvoiceReviewNotice(true)
      setActiveTab('invoices')
      loadInvoices()
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error uploading invoice:', error)
      // El cliente HTTP de la app lanza { message, error, statusCode }, no la
      // forma de axios: leyendo solo `error.response.data` se perdía el motivo
      // real y el proveedor siempre veía "Error al subir la factura". Por lo
      // mismo, el bloqueo contextualizado nunca se mostraba.
      // El 422 de bloqueo trae su propio cuerpo, mas ancho que un mensaje.
      const data = (error.response?.data ?? error) as {
        message?: string | string[]
        tipo?: string
        type?: string
        [clave: string]: unknown
      }
      const status = error.response?.status ?? error.statusCode
      // 422 de bloqueo de facturación contextualizado: se muestra el mensaje con
      // instrucción de desbloqueo en vez del toast genérico.
      //
      // Se acepta `type` y `tipo`: el bloqueo por documentos financieros manda la
      // llave en español y el del paquete mensual (SP-220) en inglés. Leyendo
      // solo `tipo`, el bloqueo mensual caía al toast y el proveedor nunca sabía
      // que le faltaba su paquete del mes.
      if (status === 422 && (typeof data?.tipo === 'string' || typeof data?.type === 'string')) {
        setShowUploadModal(false)
        setBloqueoError(data)
      } else {
        const mensaje = Array.isArray(data?.message) ? data.message.join(' ') : data?.message
        toast.error(mensaje || 'Error al subir la factura', { duration: 6000 })
      }
      setUploadXml(null)
      limpiarDocumentosDeFlete()
      setUploadPdf(null)
    } finally {
      setIsUploadingInvoice(false)
    }
  }

  const handleUploadPaymentComplement = async () => {
    if (!order || !selectedPaymentId || !uploadPaymentXml) return

    setIsUploadingPaymentComplement(true)
    try {
      await purchaseOrdersService.uploadComplement(
        order.id,
        selectedPaymentId,
        uploadPaymentXml,
        uploadPaymentPdf || undefined
      )
      toast.success('Complemento subido exitosamente')
      setShowPaymentUploadModal(false)
      setUploadPaymentXml(null)
      setUploadPaymentPdf(null)
      setSelectedPaymentId(null)
      loadPayments() // Reload payments to show the new status
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error uploading payment complement:', error)
      const errorMessage = error.response?.data?.message || 'Error al subir el complemento'
      toast.error(errorMessage)
    } finally {
      setIsUploadingPaymentComplement(false)
    }
  }

  const openPaymentUploadModal = (paymentId: string) => {
    setSelectedPaymentId(paymentId)
    setUploadPaymentXml(null)
    setUploadPaymentPdf(null)
    setShowPaymentUploadModal(true)
  }

  const handleConfirmSubmit = async () => {
    if (order) {
      try {
        await purchaseOrdersService.confirmOrder(order.id, confirmComment || undefined)
        setConfirmModalOpen(false)
        setConfirmComment('')
        loadOrderDetail()
        toast.success('Orden confirmada exitosamente')
      } catch (error) {
        console.error('Error confirming order:', error)
        toast.error('Error al confirmar la orden')
      }
    }
  }

  const handleRejectSubmit = async () => {
    if (order && rejectReason.trim()) {
      try {
        await purchaseOrdersService.rejectOrder(order.id, rejectReason)
        setRejectModalOpen(false)
        setRejectReason('')
        loadOrderDetail()
        toast.success('Orden rechazada exitosamente')
      } catch (error) {
        console.error('Error rejecting order:', error)
        toast.error('Error al rechazar la orden')
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Cargando orden...</p>
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Orden no encontrada</p>
        <button onClick={onBack} className={estiloDeBoton('secundario', 'mt-4')}>
          Volver
        </button>
      </div>
    )
  }

  // Removed unused badge functions


  return (
    <div className="p-6 pt-10 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={onBack}
            className={estiloDeBoton('secundario', 'mb-4')}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Volver</span>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ver orden de compra</h1>
            <p className="text-sm text-gray-600 mt-1">
              Consulta los detalles para la orden de compra {order.po_number}
            </p>
          </div>
        </div>

        {order.status === 'pending' && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setRejectModalOpen(true)}
              className={estiloDeBoton('peligro')}
            >
              <XCircle className="w-4 h-4" />
              Rechazar
            </button>
            <button
              onClick={() => setConfirmModalOpen(true)}
              className={estiloDeBoton('exito')}
            >
              <CheckCircle2 className="w-4 h-4" />
              Confirmar
            </button>
          </div>
        )}
      </div>

      {/* Order Info Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <FileText className="w-6 h-6" style={{ color: "#2563eb" }} />
            <span className="text-lg font-medium text-gray-900">
              {order.po_number}
            </span>
            <div className="flex items-center gap-2 text-gray-500 ml-2">
              <CalendarFolded className="w-4 h-4" />
              <span className="text-sm">
                {new Date(order.created_at).toLocaleDateString('es-MX')}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge color={statusDisplay.color}>
              {statusDisplay.label}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-8">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Importe total</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              {formatCurrency(Number(order.total), order.currency)}
            </p>
          </div>
          {/* <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Centro de costos:</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              {order.cost_center || 'N/A'}
            </p>
          </div> */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Fecha requerida</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              {new Date(order.delivery_date).toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' })}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Moneda</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              {order.currency}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Total pagado</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              {formatCurrency(totalPaid, order.currency)} / {formatCurrency(Number(order.total), order.currency)}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Creada por</p>
            <p className="text-base font-normal text-gray-900 dark:text-white">
              {nombreDeUsuario(order.creator ?? order.creatorHistorico)}
            </p>
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Descripción</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {order.description || 'Sin descripción'}
          </p>
        </div>
      </div>



      {/* Actions Toolbar */}
      {
        activeTab === 'lines' && (
          <div className="flex justify-end gap-4 mb-4">
            <div className="flex flex-col mr-auto">
              {isSelectionMode && (
                <span className="text-sm text-gray-500">
                  {selectedLineIds.size} seleccionados
                </span>
              )}
              <span className="text-sm text-blue-700 font-medium">
                Monto disponible para facturar: {formatCurrency(availableAmount, order.currency)}
              </span>
            </div>

            {!isSelectionMode ? (
              <button
                onClick={async () => {
                  // Antes de dejarlo elegir lineas: si esta bloqueado, se le
                  // dice aqui y no llega ni al modal de subir.
                  if (!(await puedeFacturar())) return
                  const loadingToast = toast.loading('Habilitando selección...')
                  refreshAvailableLines()
                    .then(availableIds => {
                      if (!availableIds) {
                        toast.error('Error al cargar las líneas disponibles', { id: loadingToast })
                        return
                      }
                      setIsSelectionMode(true)
                      if (availableIds.size === 0) {
                        toast('No hay líneas disponibles para facturar', { icon: 'ℹ️', id: loadingToast })
                      } else {
                        toast.dismiss(loadingToast)
                      }
                    })
                    .catch(error => {
                      console.error('Error loading available lines:', error)
                      toast.error('Error al cargar las líneas disponibles', { id: loadingToast })
                    })
                }}
                className={estiloDeBoton('primario')}
              >
                <Upload className="w-4 h-4" />
                Subir Factura
              </button>
            ) : (
              <>
                <button
                  onClick={toggleSelectionMode}
                  className={estiloDeBoton('secundario')}
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => { if (await puedeFacturar()) setShowUploadModal(true) }}
                  disabled={selectedLineIds.size === 0}
                  className={estiloDeBoton('primario')}
                >
                  <Upload className="w-4 h-4" />
                  Subir Factura
                </button>
              </>
            )}
          </div>
        )
      }

      {/* Tabs */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab('lines')}
          className={`px-6 py-2.5 font-medium text-sm transition-all rounded-lg border ${activeTab === 'lines'
            ? 'bg-[#2C3E7C] text-white border-[#2C3E7C]'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
        >
          Líneas de productos
        </button>
        <button
          onClick={() => setActiveTab('invoices')}
          className={`px-6 py-2.5 font-medium text-sm transition-all rounded-lg border ${activeTab === 'invoices'
            ? 'bg-[#2C3E7C] text-white border-[#2C3E7C]'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
        >
          Facturas cargadas
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={`px-6 py-2.5 font-medium text-sm transition-all rounded-lg border ${activeTab === 'payments'
            ? 'bg-[#2C3E7C] text-white border-[#2C3E7C]'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
        >
          Historial de pagos
        </button>


      </div>


      {/* Tab Content */}
      {
        activeTab === 'lines' && (
          <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
            {isRefreshingLineAvailability && (
              <div className="px-6 py-3 border-b border-blue-100 bg-blue-50 text-blue-700 text-sm font-medium">
                Actualizando disponibilidad de líneas...
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[#E5E7EB]">
                <thead className="bg-[#FAF9FF]">
                  <tr>
                    {isSelectionMode && (
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        <Checkbox
                          checked={
                            availableLineIds.size > 0 &&
                            [...availableLineIds].every(id => selectedLineIds.has(id))
                          }
                          onChange={toggleSelectAll}
                          disabled={availableLineIds.size === 0}
                        />
                      </th>
                    )}
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">#</th>
                    <th className="px-6 py-4 min-w-[140px] text-left text-sm font-medium text-[#1B2128] tracking-wider">Código</th>
                    <th className="px-6 py-4 min-w-[220px] text-left text-sm font-medium text-[#1B2128] tracking-wider">Descripción</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">Cantidad</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">Surtidos</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Unidad</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estado</th>
                    <th className="px-6 py-4 min-w-[145px] text-right text-sm font-medium text-[#1B2128] tracking-wider">Precio Unitario</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-[#1B2128] tracking-wider">Descuento %</th>
                    <th className="px-6 py-4 min-w-[145px] text-right text-sm font-medium text-[#1B2128] tracking-wider">Subtotal</th>
                    <th className="px-6 py-4 min-w-[145px] text-right text-sm font-medium text-[#1B2128] tracking-wider">IVA</th>
                    <th className="px-6 py-4 min-w-[145px] text-right text-sm font-medium text-[#1B2128] tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E1E1E1]">
                  {order.lines?.map((line: PurchaseOrderLine) => (
                    <tr
                      key={line.id}
                      onClick={() => handleRowClick(line.id)}
                      className="border-b !border-[#e5e7eb] hover:bg-[#F4F7FB] transition-colors last:border-b-0 cursor-pointer"
                    >
                      {isSelectionMode && (
                        <td className="px-6 py-4 text-sm" onClick={(e) => e.stopPropagation()}>
                          {availableLineIds.has(line.id) ? (
                            <Checkbox
                              checked={selectedLineIds.has(line.id)}
                              onChange={() => toggleLineSelection(line.id)}
                            />
                          ) : (
                            <div className="w-4 h-4"></div> // Placeholder for alignment
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{line.line_number}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                        <TruncatedText text={line.product_code} maxWidthClass="max-w-[130px]" className="text-gray-900 dark:text-white" />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                        <TruncatedText text={line.product_name} maxWidthClass="max-w-[220px]" className="text-gray-900 dark:text-white" />
                      </td>
                      <td className="px-6 py-4 text-sm text-center text-gray-900 dark:text-white">{line.quantity}</td>
                      <td className="px-6 py-4 text-sm text-center text-gray-500 dark:text-gray-400">
                        {Number(line.quantity_received || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{line.unit_of_measure}</td>
                      <td className="px-6 py-4 text-sm">
                        {(() => {
                          const hasActiveInvoice = ((line as any).invoices || []).some((inv: any) =>
                            ['pending', 'approved', 'in_review', 'received'].includes(inv.status)
                          )
                          if (hasActiveInvoice) {
                            return (
                              <Badge color="verde">Facturado</Badge>
                            )
                          }
                          const isReleased = (line as any).erp_status === 'LIBERADA_PARA_PROVEEDOR'
                          if (isReleased) {
                            return (
                              <Badge color="azul">Liberada</Badge>
                            )
                          }
                          return (
                            <Badge color="gris">No liberada</Badge>
                          )
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">{formatCurrency(line.unit_price, order.currency)}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">{line.discount_percent}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">{formatCurrency(line.subtotal, order.currency)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">{formatCurrency(line.tax_amount, order.currency)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(line.total, order.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      }



      {
        activeTab === 'payments' && (
          <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
            {isLoadingPayments ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-600 ml-3">Cargando pagos...</p>
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No hay pagos registrados para esta orden</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#E5E7EB]">
                  <thead className="bg-[#FAF9FF]">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Fecha</th>
                      <th className="px-6 py-4 text-right text-sm font-medium text-[#1B2128] tracking-wider">Monto</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Método</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Referencia</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Complemento</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estatus</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Registrado por</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Notas</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#E1E1E1]">
                    {payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-[#F4F7FB] transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-900">{formatDate(payment.payment_date)}</td>
                        <td className="px-6 py-4 text-sm text-right font-medium text-gray-900">{formatCurrency(payment.amount)}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{payment.payment_method || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{payment.reference_number || '-'}</td>
                        <td className="px-6 py-4 text-sm">
                          {payment.complement_xml_url || payment.complement_pdf_url ? (
                            <div className="flex gap-2">
                              {payment.complement_xml_url && (
                                <a
                                  href={payment.complement_xml_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-700 text-xs"
                                >
                                  XML
                                </a>
                              )}
                              {payment.complement_pdf_url && (
                                <a
                                  href={payment.complement_pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-700 text-xs"
                                >
                                  PDF
                                </a>
                              )}
                            </div>
                          ) : (
                            (!payment.complement_status || (payment.complement_status !== 'validated' && !payment.complement_xml_url)) ? (
                              <button
                                onClick={() => openPaymentUploadModal(payment.id)}
                                className={estiloDeBoton('secundario')}
                              >
                                <Upload className="w-4 h-4" />
                                Subir
                              </button>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {payment.complement_status === 'validated' && (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle2 className="w-4 h-4" />
                              <span className="text-xs font-medium">Validado</span>
                            </div>
                          )}
                          {payment.complement_status === 'rejected' && (
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-red-600">
                                <XCircle className="w-4 h-4" />
                                <span className="text-xs font-medium">Rechazado</span>
                              </div>
                              {payment.complement_validation_notes && (
                                <p className="text-xs text-red-600 max-w-[200px]">{payment.complement_validation_notes}</p>
                              )}
                              <button
                                onClick={() => openPaymentUploadModal(payment.id)}
                                className="text-xs text-blue-600 hover:text-blue-800 underline mt-1"
                              >
                                Re-subir
                              </button>
                            </div>
                          )}
                          {payment.complement_status === 'pending' && (payment.complement_xml_url || payment.complement_pdf_url) && (
                            <Badge color="amarillo">Pendiente</Badge>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{nombreDeUsuario(payment.createdBy ?? payment.createdByHistorico)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{payment.notes || '-'}</td>
                      </tr>

                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      }

      {
        activeTab === 'invoices' && (
          <div className="space-y-6">
            {showInvoiceReviewNotice && (
              <div className="bg-[#FFF7E6] border border-[#F6D585] rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-[#8A5A00]">
                      Factura recibida. Estatus actual: Recibida
                    </p>
                    <p className="text-sm text-[#8A5A00] mt-1">
                      Tu factura ya fue cargada y pasará a En revisión cuando Admin inicie su dictamen.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowInvoiceReviewNotice(false)}
                    className="text-[#8A5A00] hover:text-[#6f4700] transition-colors"
                    title="Cerrar aviso"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Upload Invoice Actions */}
            <div className="flex justify-between items-center bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-4">
                <h3 className="font-semibold text-gray-900">Gestión de Facturas</h3>
                {isSelectionMode && (
                  <span className="text-sm text-gray-500">
                    {selectedLineIds.size} productos seleccionados
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                {!isSelectionMode ? (
                  <button
                    onClick={toggleSelectionMode}
                    disabled={!canUploadAnotherInvoice}
                    className={estiloDeBoton('primario')}
                    title={
                      canUploadAnotherInvoice
                        ? hasApprovedInvoice
                          ? 'Subir factura complementaria para líneas aún no facturadas'
                          : 'Subir factura para líneas habilitadas'
                        : 'No hay líneas disponibles para cargar otra factura'
                    }
                  >
                    <Upload className="w-4 h-4" />
                    {hasApprovedInvoice ? 'Subir factura complementaria' : 'Subir Factura'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={toggleSelectionMode}
                      className={estiloDeBoton('secundario')}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={async () => { if (await puedeFacturar()) setShowUploadModal(true) }}
                      disabled={selectedLineIds.size === 0}
                      className={estiloDeBoton('primario')}
                    >
                      Continuar
                    </button>
                  </>
                )}
              </div>
            </div>
            {!canUploadAnotherInvoice && (
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4">
                <p className="text-sm text-[#334155]">
                  Ya existe al menos una factura aprobada y no hay líneas adicionales habilitadas para facturar.
                </p>
              </div>
            )}
            {canUploadAnotherInvoice && hasApprovedInvoice && !isSelectionMode && (
              <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-4">
                <p className="text-sm text-[#1E40AF]">
                  Esta orden de compra ya tiene facturas aprobadas. La siguiente carga será tratada como factura complementaria para líneas pendientes.
                </p>
              </div>
            )}

            {/* Invoices List */}
            {isLoadingInvoices ? (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <p className="text-sm text-gray-600 ml-3">Cargando facturas...</p>
                </div>
              </div>
            ) : invoices.length > 0 ? (
              <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#E5E7EB]">
                    <thead className="bg-[#FAF9FF]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Factura</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estatus</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">XML</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">PDF</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Notas</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Archivos</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Partidas relacionadas</th>
                        <th className="px-6 py-4 text-right text-sm font-medium text-[#1B2128] tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E1E1E1]">
                      {invoices.map((invoice, index) => (
                        <tr
                          key={invoice.id}
                          className={`transition-colors ${!invoice.xml_url || !invoice.pdf_url ? 'bg-red-50/40 hover:bg-red-50/60' : 'hover:bg-[#F4F7FB]'
                            }`}
                        >
                          <InvoiceSharedCells
                            invoice={invoice}
                            index={index}
                            formatDate={formatDate}
                            statusMeta={getInvoiceStatusMeta(invoice.status)}
                            etiquetaComplemento={etiquetaDeFactura(invoice)}
                            onViewLines={() => {
                              setSelectedInvoice(invoice)
                              setShowInvoiceLinesModal(true)
                            }}
                          />
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              {['rejected', 'observed'].includes(invoice.status || '') ? (
                                <button
                                  onClick={async () => {
                                    if (!(await puedeFacturar())) return
                                    setReplaceInvoiceId(invoice.id)
                                    setShowUploadModal(true)
                                  }}
                                  className={estiloDeBoton('primario')}
                                >
                                  <Upload className="w-4 h-4" />
                                  Re-subir
                                </button>
                              ) : (!invoice.xml_url || !invoice.pdf_url) && (
                                <button
                                  onClick={async () => {
                                    if (!(await puedeFacturar())) return
                                    setSelectedLineIds(new Set(((invoice as any).lines || []).map((l: any) => l.id)))
                                    setShowUploadModal(true)
                                  }}
                                  className={estiloDeBoton('primario')}
                                >
                                  <Upload className="w-4 h-4" />
                                  {!invoice.xml_url && !invoice.pdf_url ? 'Subir XML y PDF' : !invoice.xml_url ? 'Subir XML' : 'Subir PDF'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <p className="text-gray-500">No hay facturas cargadas</p>
              </div>
            )}

            {/* Attachments Section */}
            {isLoadingAttachments ? (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <p className="text-sm text-gray-600 ml-3">Cargando adjuntos...</p>
                </div>
              </div>
            ) : (attachments.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Ver archivos adjuntos</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                  {attachments.map((attachment) => (
                    <div key={attachment.id} className="relative">
                      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setOpenAttachmentMenu(openAttachmentMenu === attachment.id ? null : attachment.id)
                          }}
                          className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          <MoreVertical className="w-4 h-4 text-blue-600" />
                        </button>

                        {openAttachmentMenu === attachment.id && (
                          <div className="absolute top-8 right-2 w-40 bg-white rounded-lg border border-gray-200 shadow-xl py-1 z-10">
                            <a
                              href={attachment.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              onClick={() => setOpenAttachmentMenu(null)}
                            >
                              <Eye className="w-4 h-4" />
                              Ver
                            </a>
                            <a
                              href={attachment.file_url}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              onClick={() => setOpenAttachmentMenu(null)}
                            >
                              <Download className="w-4 h-4" />
                              Descargar
                            </a>
                          </div>
                        )}

                        <div className="flex flex-col items-center">
                          <div className="w-16 h-16 mb-3 flex items-center justify-center">
                            <FileText className="w-16 h-16 text-blue-600" />
                          </div>
                          <p className="text-sm text-gray-900 text-center truncate w-full" title={attachment.description || 'archivo'}>
                            {attachment.description || 'archivo'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      }



      {/* Payment Complement Upload Modal */}
      {
        showPaymentUploadModal && (
          <CapaDeModal onClickFondo={() => setShowPaymentUploadModal(false)}>
              <div className="w-full max-w-lg bg-white rounded-lg text-left overflow-hidden shadow-xl">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Subir Complemento de Pago
                      </h3>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            XML del Complemento <span className="text-red-500">*</span>
                          </label>
                          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                            <div className="space-y-1 text-center">
                              <Upload className="mx-auto h-12 w-12 text-gray-400" />
                              <div className="flex text-sm text-gray-600">
                                <label htmlFor="payment-xml-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                  <span>Subir archivo XML</span>
                                  <input
                                    id="payment-xml-upload"
                                    name="payment-xml-upload"
                                    type="file"
                                    className="sr-only"
                                    onChange={(e) => {
                                      if (e.target.files && e.target.files[0]) {
                                        const file = e.target.files[0]
                                        if (file.type === 'text/xml' || file.type === 'application/xml' || file.name.toLowerCase().endsWith('.xml')) {
                                          setUploadPaymentXml(file)
                                        } else {
                                          toast.error('Por favor selecciona un archivo XML válido')
                                          e.target.value = ''
                                        }
                                      }
                                    }}
                                  />
                                </label>
                              </div>
                              <p className="text-xs text-gray-500">
                                {uploadPaymentXml ? uploadPaymentXml.name : 'Selecciona un archivo XML'}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            PDF del Complemento (Opcional)
                          </label>
                          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                            <div className="space-y-1 text-center">
                              <FileText className="mx-auto h-12 w-12 text-gray-400" />
                              <div className="flex text-sm text-gray-600">
                                <label htmlFor="payment-pdf-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                  <span>Subir archivo PDF</span>
                                  <input
                                    id="payment-pdf-upload"
                                    name="payment-pdf-upload"
                                    type="file"
                                    className="sr-only"
                                    onChange={(e) => {
                                      if (e.target.files && e.target.files[0]) {
                                        const file = e.target.files[0]
                                        if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
                                          setUploadPaymentPdf(file)
                                        } else {
                                          toast.error('Por favor selecciona un archivo PDF válido')
                                          e.target.value = ''
                                        }
                                      }
                                    }}
                                  />
                                </label>
                              </div>
                              <p className="text-xs text-gray-500">
                                {uploadPaymentPdf ? uploadPaymentPdf.name : 'Selecciona un archivo PDF'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={!uploadPaymentXml || isUploadingPaymentComplement}
                    onClick={handleUploadPaymentComplement}
                    className={estiloDeBoton('primario', 'w-full sm:ml-3 sm:w-auto')}
                  >
                    {isUploadingPaymentComplement ? 'Subiendo...' : 'Subir Complemento'}
                  </button>
                  <button
                    type="button"
                    disabled={isUploadingPaymentComplement}
                    onClick={() => setShowPaymentUploadModal(false)}
                    className={estiloDeBoton('secundario', 'mt-3 w-full sm:mt-0 sm:ml-3 sm:w-auto')}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
          </CapaDeModal>
        )
      }


      {
        order.status === 'rejected' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Motivo del rechazo</h3>
            <div className="flex items-start gap-2 mb-2">
              <svg className="w-4 h-4 text-gray-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              <span className="text-xs text-gray-400">Nora Robles</span>
            </div>
            <p className="text-sm text-gray-700">
              Lorem ipsum dolor sit amet consectetur. Arcu commodo sed sed nibh amet adipiscing faucibus nec non. Maecenas sed facilisis nullam sit egestas senectus sem ipsum.
            </p>
          </div>
        )
      }
      {/* Subir factura. El portal lo hace CapaDeModal. */}
      {
        showUploadModal && (
          <CapaDeModal>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#1E1E1F]">Subir factura</h3>
                  <button
                    onClick={() => {
                      setShowUploadModal(false)
                      setUploadXml(null)
                      limpiarDocumentosDeFlete()
                      setUploadPdf(null)
                      setReplaceInvoiceId(null)
                      setXmlValidationResult(null)
                    }}
                    className="bg-[#D9D9D9]/60 rounded-full p-1 text-[#BDBDBD] hover:text-[#000] transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Subiendo factura para <span className="font-semibold">{selectedLineIds.size}</span> productos seleccionados
                  </p>
                  <p className="text-sm text-blue-700 font-medium mb-1">
                    Monto disponible para facturar: {formatCurrency(availableAmount, order.currency)}
                  </p>
                  <p className="text-sm text-gray-700">
                    Monto de líneas seleccionadas: {formatCurrency(selectedAmount, order.currency)}
                  </p>
                </div>

                {/* Bloqueo (nuevo): facturas pagadas sin CFDI de complemento de pago */}
                {complementoBloqueo && (
                  <div className="mb-4 flex items-start gap-2 p-4 rounded-lg bg-[#FFFCEE] border border-[#F2E2A8] text-[#BD8F0E]">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold">No puedes subir facturas</p>
                      <p className="text-sm mt-0.5">
                        Tienes {complementoBloqueo.count} facturas pagadas sin CFDI de complemento de pago. Súbelo para desbloquear tu facturación.
                      </p>
                      <button
                        type="button"
                        onClick={() => { window.location.href = '/proveedores/ordenes?facturas=pagada_sin_complemento' }}
                        className="text-sm font-medium underline mt-1 hover:opacity-80"
                      >
                        Ir al listado de facturas afectadas
                      </button>
                    </div>
                  </div>
                )}

                {/* Validation Result Panel */}
                {(xmlValidationResult || isValidatingXml) && (
                  (() => {
                    const allValid = xmlValidationResult
                      ? xmlValidationResult.xmlReadable && xmlValidationResult.estructuraCorrecta && xmlValidationResult.rfcEmisorCorrecto && xmlValidationResult.rfcReceptorCorrecto && xmlValidationResult.montoValido
                      : false
                    const montoOkText = xmlValidationResult?.xmlTotal !== null && xmlValidationResult?.xmlTotal !== undefined
                      ? `Correcto (${formatCurrency(xmlValidationResult.xmlTotal, order.currency)})`
                      : 'Correcto'
                    const montoFailText = xmlValidationResult?.xmlTotal !== null && xmlValidationResult?.xmlTotal !== undefined
                      ? `${formatCurrency(xmlValidationResult.xmlTotal, order.currency)} ≠ ${formatCurrency(selectedAmount, order.currency)}`
                      : 'No se pudo leer'
                    return (
                      <div className={`mb-4 border rounded-lg ${allValid ? 'border-green-200 bg-green-50' : 'border-[#F2E2A8] bg-[#FFFCEE]'}`}>
                        <button
                          type="button"
                          onClick={() => setIsValidationPanelOpen(prev => !prev)}
                          className="w-full flex items-center justify-between p-4 text-left"
                        >
                          <div className="flex items-center gap-2">
                            {allValid ? (
                              <Info className="w-4 h-4 text-green-600" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-[#BD8F0E]" />
                            )}
                            <span className={`text-sm font-semibold ${allValid ? 'text-gray-700' : 'text-[#BD8F0E]'}`}>Resultado de la validación</span>
                          </div>
                          <ChevronUp className={`w-4 h-4 transition-transform duration-200 ${isValidationPanelOpen ? '' : 'rotate-180'} ${allValid ? 'text-green-600' : 'text-[#BD8F0E]'}`} />
                        </button>
                        {isValidationPanelOpen && (
                          <div className="px-4 pb-4">
                            {isValidatingXml ? (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                                Validando archivo...
                              </div>
                            ) : xmlValidationResult && (
                              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                                {([
                                  { label: 'Lectura XML', ok: xmlValidationResult.xmlReadable, okText: 'Exitosa', failText: 'Error' },
                                  { label: 'RFC Emisor', ok: xmlValidationResult.rfcEmisorCorrecto, okText: 'Correcto', failText: 'Incorrecto' },
                                  { label: 'Estructura', ok: xmlValidationResult.estructuraCorrecta, okText: 'Correcta', failText: 'Incorrecta' },
                                  { label: 'RFC Receptor', ok: xmlValidationResult.rfcReceptorCorrecto, okText: 'Correcto', failText: 'Incorrecto' },
                                ] as const).map(({ label, ok, okText, failText }) => (
                                  <div key={label} className="flex items-center gap-2 text-sm">
                                    <span className={allValid ? 'text-gray-600' : 'text-[#BD8F0E]'}>{label}:</span>
                                    <span className={ok ? 'text-green-600 font-medium' : 'text-[#BD8F0E] font-medium'}>
                                      {ok ? okText : failText}
                                    </span>
                                  </div>
                                ))}
                                {!replaceInvoiceId && (
                                  <div className="flex items-center gap-2 text-sm col-span-2">
                                    <span className={allValid ? 'text-gray-600' : 'text-[#BD8F0E]'}>Monto factura:</span>
                                    <span className={xmlValidationResult.montoValido ? 'text-green-600 font-medium' : 'text-[#BD8F0E] font-medium'}>
                                      {xmlValidationResult.montoValido ? montoOkText : montoFailText}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })()
                )}

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Archivo XML <span className="text-red-500">*</span>
                    </label>
                    <div className="relative flex items-center justify-center w-full">
                      <label className="flex flex-col items-center justify-center gap-2 w-full h-28 rounded-lg cursor-pointer bg-[#F6F9FB] border border-dashed border-gray-200 hover:bg-[#eef2f7] transition-colors">
                        {uploadXml ? (
                          <div className="flex items-center gap-2 px-4">
                            <FileText className="w-5 h-5 text-blue-500 shrink-0" />
                            <p className="text-sm text-gray-600 truncate">{uploadXml.name}</p>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-gray-400" />
                            <p className="text-sm text-gray-400">Click para seleccionar XML</p>
                          </>
                        )}
                        <input
                          type="file"
                          accept=".xml"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) {
                              setUploadXml(file)
                              setIsValidationPanelOpen(true)
                              validateCfdiXml(file)
                            }
                          }}
                        />
                      </label>
                      {uploadXml && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setUploadXml(null)
                            setXmlValidationResult(null)
                          }}
                          className="absolute top-2 right-3 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cargar archivo PDF <span className="text-gray-400">(opcional)</span>
                    </label>
                    <div className="relative flex items-center justify-center w-full">
                      <label className="flex flex-col items-center justify-center gap-2 w-full h-28 rounded-lg cursor-pointer bg-[#F6F9FB] border border-dashed border-gray-200 hover:bg-[#eef2f7] transition-colors">
                        {uploadPdf ? (
                          <div className="flex items-center gap-2 px-4">
                            <FileText className="w-5 h-5 text-red-500 shrink-0" />
                            <p className="text-sm text-gray-600 truncate">{uploadPdf.name}</p>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-gray-400" />
                            <p className="text-sm text-gray-400">Click para seleccionar PDF</p>
                          </>
                        )}
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && setUploadPdf(e.target.files[0])}
                        />
                      </label>
                      {uploadPdf && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setUploadPdf(null)
                          }}
                          className="absolute top-2 right-3 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Paso de documentos de flete (SP-221): solo para tipo Fletes.
                    El servidor exige estos documentos, así que sin este bloque la
                    factura no se puede subir desde esta pantalla. */}
                {esFletes && (
                  <div className="border-t border-gray-200 pt-4 mb-6 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900">Documentos de flete</h4>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">¿Esta factura incluye Carta Porte?</span>
                      <button
                        type="button"
                        onClick={() => setCartaPorteAplica((valor) => !valor)}
                        className="flex items-center gap-2"
                        aria-label="¿Esta factura incluye Carta Porte?"
                      >
                        <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${cartaPorteAplica ? 'bg-[#253579]' : 'bg-gray-300'}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cartaPorteAplica ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </span>
                        <span className="text-sm text-gray-600 w-5 text-left">{cartaPorteAplica ? 'Sí' : 'No'}</span>
                      </button>
                    </div>

                    {cartaPorteAplica ? (
                      <>
                        <ArchivoDeFlete
                          etiqueta="Carta Porte (XML)"
                          requerido
                          acepta=".xml"
                          archivo={cartaPorteXml}
                          onElegir={setCartaPorteXml}
                        />
                        <ArchivoDeFlete
                          etiqueta="Carta Porte (PDF)"
                          requerido
                          acepta=".pdf"
                          archivo={cartaPortePdf}
                          onElegir={setCartaPortePdf}
                        />
                      </>
                    ) : (
                      <p className="text-xs text-gray-500">
                        Queda registrado que esta factura no incluye Carta Porte.
                      </p>
                    )}

                    <p className="text-xs text-gray-500 pt-1">
                      Soporte firmado: se requiere al menos uno de los dos.
                    </p>
                    <ArchivoDeFlete
                      etiqueta="Remisión firmada"
                      requerido={false}
                      acepta=".pdf"
                      archivo={remisionFirmada}
                      onElegir={setRemisionFirmada}
                    />
                    <ArchivoDeFlete
                      etiqueta="Hoja de entregas con acuse SLYRSA"
                      requerido={false}
                      acepta=".pdf"
                      archivo={hojaEntregas}
                      onElegir={setHojaEntregas}
                    />
                  </div>
                )}

                {(() => {
                  const validationPassed = xmlValidationResult
                    ? xmlValidationResult.xmlReadable && xmlValidationResult.estructuraCorrecta && xmlValidationResult.rfcEmisorCorrecto && xmlValidationResult.rfcReceptorCorrecto && xmlValidationResult.montoValido
                    : true
                  return (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setShowUploadModal(false)
                          setUploadXml(null)
                          limpiarDocumentosDeFlete()
                          setUploadPdf(null)
                          setReplaceInvoiceId(null)
                          setXmlValidationResult(null)
                        }}
                        className={estiloDeBoton('secundario', 'flex-1')}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleUploadInvoice}
                        disabled={!uploadXml || isUploadingInvoice || isValidatingXml || !validationPassed || !fleteResuelto}
                        className={estiloDeBoton('primario', 'flex-1')}
                      >
                        {isUploadingInvoice ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        Subir
                      </button>
                    </div>
                  )
                })()}
              </div>
            </div>
          </CapaDeModal>
        )
      }

      {/* Confirm Modal */}
      {
        confirmModalOpen && (
          <CapaDeModal>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Confirmar orden</h3>
                  <button
                    onClick={() => {
                      setConfirmModalOpen(false)
                      setConfirmComment('')
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  ¿Estás seguro de confirmar la orden <span className="font-semibold">{order?.po_number}</span>?
                </p>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comentario <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <textarea
                    value={confirmComment}
                    onChange={(e) => setConfirmComment(e.target.value)}
                    placeholder="Ej: Entrega estimada en 5 días hábiles"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm resize-none"
                    style={{ backgroundColor: '#F9FAFB' }}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setConfirmModalOpen(false)
                      setConfirmComment('')
                    }}
                    className={estiloDeBoton('secundario', 'flex-1')}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmSubmit}
                    className={estiloDeBoton('exito', 'flex-1')}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </CapaDeModal>
        )
      }

      {/* Reject Modal */}
      {
        rejectModalOpen && (
          <CapaDeModal>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Motivo del rechazo</h3>
                  <button
                    onClick={() => {
                      setRejectModalOpen(false)
                      setRejectReason('')
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">Ingresa el motivo de rechazo para la orden</p>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Motivo</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Ingresa aquí el motivo de rechazo..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm resize-none"
                    style={{ backgroundColor: '#F9FAFB' }}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setRejectModalOpen(false)
                      setRejectReason('')
                    }}
                    className={estiloDeBoton('secundario', 'flex-1')}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleRejectSubmit}
                    disabled={!rejectReason.trim()}
                    className={estiloDeBoton('peligro', 'flex-1')}
                  >
                    <XCircle className="w-4 h-4" />
                    Rechazar
                  </button>
                </div>
              </div>
            </div>
          </CapaDeModal>
        )
      }
      {/* Invoice Lines Modal */}
      {
        showInvoiceLinesModal && selectedInvoice && (
          <CapaDeModal>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 overflow-hidden flex flex-col max-h-[90vh]">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-white sticky top-0 z-10">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-gray-900">Factura #{invoices.indexOf(selectedInvoice) + 1}</h3>
                  <Badge color={colorDeEstatusFactura(selectedInvoice.status)}>
                    {getInvoiceStatusMeta(selectedInvoice.status).label}
                  </Badge>
                </div>
                <button
                  onClick={() => setShowInvoiceLinesModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto">
                <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
                  <table className="min-w-full divide-y divide-[#E5E7EB]">
                    <thead className="bg-[#FAF9FF]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">#</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Código</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Descripción</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Cantidad</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Unidad</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Precio.unit</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Desc.%</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Subtotal</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">IVA</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E1E1E1]">
                      {selectedInvoice?.lines?.map((line: any) => (
                        <tr key={line.id} className="hover:bg-[#F4F7FB] transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{line.line_number}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{line.product_code}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">{line.product_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{line.quantity}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${Number(line.unit_price).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">0%</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${(Number(line.quantity) * Number(line.unit_price)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">-</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${Number(line.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                      {(!selectedInvoice?.lines || selectedInvoice?.lines?.length === 0) && (
                        <tr>
                          <td colSpan={10} className="px-6 py-10 text-center text-gray-500">
                            No hay partidas asociadas a esta factura.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </CapaDeModal>
        )
      }

      {/* Bloqueo de facturación contextualizado (422 con tipo, SP-223) */}
      <BloqueoFacturacion
        open={bloqueoError !== null}
        error={bloqueoError}
        onClose={() => setBloqueoError(null)}
      />
    </div >
  )
}

export default PurchaseOrderDetail
